import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Scale, Menu, X, LogOut, User as UserIcon, Shield, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/incarcerated", label: "Incarcerated" },
  { to: "/families", label: "Families" },
  { to: "/defense", label: "Defense" },
  { to: "/hearings", label: "Hearings" },
  { to: "/attorneys", label: "Attorneys" },
  { to: "/community", label: "Community" },
  { to: "/faq", label: "FAQ" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors link-underline ${
      isActive ? "text-amber-400 font-medium" : "text-zinc-300 hover:text-amber-400"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-zinc-950 border-b border-zinc-800"
      data-testid="site-header"
    >
      <div className="container-pl">
        <div className="flex h-16 items-center justify-between gap-6">
          <Link
            to="/"
            className="flex items-center gap-2.5 text-zinc-50"
            data-testid="logo-link"
          >
            <Scale className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
            <div className="leading-tight">
              <p className="font-medium tracking-tight text-sm sm:text-base text-zinc-50">
                Paralegal Voices
              </p>
              <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-amber-400 font-medium">
                of New York
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-sm" data-testid="user-menu-trigger">
                    <UserIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    {user.name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-sm">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col">
                      <span className="text-sm">{user.name}</span>
                      <span className="text-xs text-zinc-400">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.role === "admin" ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Admin Vault
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="menu-portal">
                      <LayoutDashboard className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Client Portal
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                    data-testid="menu-logout"
                  >
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="text-sm text-zinc-300 hover:text-amber-400 link-underline" data-testid="login-link">
                  Client Login
                </Link>
                <Button
                  onClick={() => navigate("/booking")}
                  className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white"
                  data-testid="header-schedule-btn"
                >
                  Schedule Consultation
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-zinc-800 py-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-sm ${isActive ? "text-amber-400 font-medium" : "text-zinc-200"}`
                  }
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-2 pt-2 border-t border-zinc-800">
                {user ? (
                  <>
                    {user.role === "admin" ? (
                      <Link to="/admin" onClick={() => setOpen(false)} className="text-sm text-amber-400">
                        Admin Vault
                      </Link>
                    ) : (
                      <Link to="/portal" onClick={() => setOpen(false)} className="text-sm text-amber-400">
                        Client Portal
                      </Link>
                    )}
                    <button
                      onClick={async () => {
                        await logout();
                        setOpen(false);
                        navigate("/");
                      }}
                      className="text-left text-sm text-zinc-200"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="text-sm" data-testid="mobile-login-link">
                      Client Login
                    </Link>
                    <Link to="/booking" onClick={() => setOpen(false)} className="text-sm font-medium text-amber-400">
                      Schedule Consultation
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
